import { ThemeSetter } from '@/components/ThemeSetter';
import { DebugScript, TagadaProvider } from '@tagadapay/plugin-sdk/v2';
import React from 'react';
import { PaymentMethodsProvider } from '../contexts/PaymentMethods';

interface ProvidersProps {
  children: React.ReactNode;
}

// Lazy-load debug scripts (keeps @faker-js/faker out of the production bundle)
const loadDebugScripts = () => import('../../debug-mode/scripts/prefill-checkout-form');

// Debug scripts for development/testing -- loaded on-demand only when triggered
const debugScripts: DebugScript[] = [
  {
    id: 'quick-prefill',
    name: 'Quick Prefill',
    description: 'Fast prefill with random datas generated by faker.js',
    category: 'Forms',
    run: async () => {
      const { default: prefillCheckoutForm } = await loadDebugScripts();
      await prefillCheckoutForm();
    },
  },
  {
    id: 'quick-prefill-stripe-4242',
    name: 'Quick Prefill – Stripe 4242',
    description: 'Fast prefill using Stripe Visa test card 4242 4242 4242 4242',
    category: 'Forms',
    run: async () => {
      const { default: prefillCheckoutForm } = await loadDebugScripts();
      await prefillCheckoutForm(undefined, '4242 4242 4242 4242');
    },
  },
  {
    id: 'card-network-visa',
    name: 'Card Network – Visa',
    description: 'Fill only the card number field with a Visa test card',
    category: 'Card Network',
    run: async () => {
      const { prefillCardForNetwork } = await loadDebugScripts();
      await prefillCardForNetwork('visa');
    },
  },
  {
    id: 'card-network-mastercard',
    name: 'Card Network – Mastercard',
    description: 'Fill only the card number field with a Mastercard test card',
    category: 'Card Network',
    run: async () => {
      const { prefillCardForNetwork } = await loadDebugScripts();
      await prefillCardForNetwork('mastercard');
    },
  },
  {
    id: 'card-network-maestro',
    name: 'Card Network – Maestro',
    description: 'Fill only the card number field with a Maestro test card',
    category: 'Card Network',
    run: async () => {
      const { prefillCardForNetwork } = await loadDebugScripts();
      await prefillCardForNetwork('maestro');
    },
  },
  {
    id: 'card-network-amex',
    name: 'Card Network – Amex',
    description: 'Fill only the card number field with an American Express test card',
    category: 'Card Network',
    run: async () => {
      const { prefillCardForNetwork } = await loadDebugScripts();
      await prefillCardForNetwork('amex');
    },
  },
  {
    id: 'card-network-diners',
    name: 'Card Network – Diners',
    description: 'Fill only the card number field with a Diners Club test card',
    category: 'Card Network',
    run: async () => {
      const { prefillCardForNetwork } = await loadDebugScripts();
      await prefillCardForNetwork('diners');
    },
  },
  {
    id: 'card-network-discover',
    name: 'Card Network – Discover',
    description: 'Fill only the card number field with a Discover test card',
    category: 'Card Network',
    run: async () => {
      const { prefillCardForNetwork } = await loadDebugScripts();
      await prefillCardForNetwork('discover');
    },
  },
  {
    id: 'card-network-jcb',
    name: 'Card Network – JCB',
    description: 'Fill only the card number field with a JCB test card',
    category: 'Card Network',
    run: async () => {
      const { prefillCardForNetwork } = await loadDebugScripts();
      await prefillCardForNetwork('jcb');
    },
  },
  {
    id: 'card-network-elo',
    name: 'Card Network – Elo',
    description: 'Fill only the card number field with an Elo test card',
    category: 'Card Network',
    run: async () => {
      const { prefillCardForNetwork } = await loadDebugScripts();
      await prefillCardForNetwork('elo');
    },
  },
  {
    id: 'card-network-unionpay',
    name: 'Card Network – UnionPay',
    description: 'Fill only the card number field with a UnionPay test card',
    category: 'Card Network',
    run: async () => {
      const { prefillCardForNetwork } = await loadDebugScripts();
      await prefillCardForNetwork('unionpay');
    },
  },
  {
    id: 'card-network-mir',
    name: 'Card Network – Mir',
    description: 'Fill only the card number field with a Mir test card',
    category: 'Card Network',
    run: async () => {
      const { prefillCardForNetwork } = await loadDebugScripts();
      await prefillCardForNetwork('mir');
    },
  },
  {
    id: 'card-network-rupay',
    name: 'Card Network – RuPay',
    description: 'Fill only the card number field with a RuPay test card',
    category: 'Card Network',
    run: async () => {
      const { prefillCardForNetwork } = await loadDebugScripts();
      await prefillCardForNetwork('rupay');
    },
  },
];

export function Providers({ children }: ProvidersProps) {
  return (
    <TagadaProvider debugScripts={debugScripts}
      customApiConfig={{
        apiConfig: {
          baseUrl: 'https://crm.apptics.ai',
          endpoints: {
            checkout: {
              sessionInit: '/api/v1/checkout/session/init',
              sessionInitAsync: '/api/v1/checkout/session/init-async',
              sessionStatus: '/api/v1/checkout/session/status',
              asyncStatus: '/api/public/v1/checkout/async-status',
            },
            customer: {
              profile: '/api/v1/customer/profile',
              session: '/api/v1/customer/session',
            },
            store: {
              config: '/api/v1/store/config',
            },
          },
        },
      }}
    >
      <PaymentMethodsProvider>
        <ThemeSetter />
        {children}
      </PaymentMethodsProvider>
    </TagadaProvider>
  );
}
